import { useEffect, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { MAPS_API_KEY, LIBRARIES, CAT_META, makePinIcon, makeNumberedPinIcon } from '../../utils/mapConstants';
import type { VenueCategory } from '../../types';
import LoadingScreen from '../ui/LoadingScreen';

/**
 * A trip map nobody can edit: venue pins, numbered stops and the route line.
 *
 * Presentational on purpose — it fetches nothing. Both callers already know
 * where their data comes from and the difference matters: the community page
 * resolves coordinates against authenticated endpoints, while the public share
 * page may only draw what its payload already contained. Giving this component
 * a fetch of its own is how the second one would quietly start spending.
 */

const MAP_LOADER_ID = 'google-map-script';

export interface TripMapPin {
  id: string;
  lat: number;
  lng: number;
  title: string;
  category: VenueCategory;
}

export interface TripMapStop {
  id: string;
  lat: number;
  lng: number;
  title: string;
}

interface Props {
  center: google.maps.LatLngLiteral | null;
  /** Unnumbered category pins — the saved places. */
  pins?: TripMapPin[];
  /** Numbered pins in visit order. */
  stops?: TripMapStop[];
  /** Decoded polylines, already in map coordinates. */
  paths?: (google.maps.LatLng[] | google.maps.LatLngLiteral[])[];
  height?: number;
  /** Fired once the map is up — where the map-load beacon goes. */
  onMapLoad?: () => void;
  /** Shown instead of the map when there is no Maps key configured. */
  unavailableLabel: string;
}

export default function ReadOnlyTripMap({
  center, pins = [], stops = [], paths = [], height = 560, onMapLoad, unavailableLabel,
}: Props) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: MAPS_API_KEY ?? '',
    id: MAP_LOADER_ID,
    libraries: LIBRARIES,
  });
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Polylines are drawn imperatively so cleanup is deterministic when the day
  // changes — a declarative <Polyline> leaves the previous day's line behind
  // often enough that this was worth the extra effect.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || paths.length === 0) return;
    const map = mapRef.current;
    const polylines = paths.map(path => new window.google.maps.Polyline({
      path,
      strokeColor:   '#0d9488',
      strokeWeight:  4,
      strokeOpacity: 0.8,
      geodesic:      false,
      map,
    }));
    return () => { polylines.forEach(p => p.setMap(null)); };
  }, [paths, isLoaded]);

  // Frame what is drawn. Centring on the destination alone lands a country-wide
  // trip in its geographic middle — Iceland opened on the empty interior.
  useEffect(() => {
    const map = mapRef.current;
    const points = [...pins, ...stops];
    if (!isLoaded || !mapReady || !map || points.length === 0) return;
    if (points.length === 1) { map.setCenter(points[0]); map.setZoom(15); return; }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach(p => bounds.extend(p));
    map.fitBounds(bounds, 60);
  }, [pins, stops, isLoaded, mapReady]);

  if (!MAPS_API_KEY) {
    return (
      <div className="flex items-center justify-center h-64 bg-slate-100 dark:bg-slate-800 rounded-2xl">
        <p className="text-sm text-slate-400">{unavailableLabel}</p>
      </div>
    );
  }
  if (!isLoaded) return <LoadingScreen />;

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-100 dark:border-slate-800" style={{ height }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center ?? { lat: 40, lng: 0 }}
        zoom={center ? 13 : 3}
        onLoad={m => { mapRef.current = m; setMapReady(true); onMapLoad?.(); }}
        options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
      >
        {pins.map(p => (
          <Marker
            key={p.id}
            position={{ lat: p.lat, lng: p.lng }}
            title={p.title}
            icon={makePinIcon(CAT_META[p.category].markerColor)}
          />
        ))}
        {stops.map((s, i) => (
          <Marker
            key={`stop-${s.id}`}
            position={{ lat: s.lat, lng: s.lng }}
            title={s.title}
            icon={makeNumberedPinIcon(i + 1)}
          />
        ))}
      </GoogleMap>
    </div>
  );
}
